import { format } from 'date-fns';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import {
  Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Typography,
} from '@mui/material';
import React, {CSSProperties, useCallback, useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {UIContext} from '../../../context/UIContext';
import {ReactComponent as WhiteSuccessIcon} from '../../../img/success-white.svg';
import {ReactComponent as SuccessIcon} from '../../../img/success.svg';
import objectArrayToCSV from '../../../helpers/objectArrayToCSV';
import { TroncRow } from '../../../models/tronc';


export interface ExportDialogProps {
  start: boolean;
  onComplete: () => void;
  rows: TroncRow[];
}

const contentStyle: CSSProperties = { textAlign: 'center', width: '600px' };
const cardStyle: CSSProperties = {
  textAlign: 'right',
  justifyContent: 'flex-start',
  width: '340px',
  position: 'relative',
  left: 'calc(50% - 160px)',
  marginBottom: '32px',
};

export const ExportDialog = (props: ExportDialogProps) => {
  const { start, onComplete, rows } = props;
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [filename, setFilename] = useState<string>();
  const { colorMode } = useContext(UIContext);


  const createFileUri = () => {
    const excludeColumns: (keyof TroncRow)[] = [
      "noOfShiftRecords"
    ];
    
    const transformer = (key: string, val: unknown) => {
      if (key === "hours") return (val as number ?? 0).toFixed(2);
      if (key === "gratuity") return (val as number ?? 0).toFixed(2);
      if (key === "serviceCharge") return (val as number ?? 0).toFixed(2);
      if (key === "totalTronc") return (val as number ?? 0).toFixed(2);
      return val;
    };
    
    const blob = new Blob([objectArrayToCSV(rows, ",", "/", excludeColumns, transformer)], {type: "application/octet-stream"});
    return URL.createObjectURL(blob);
  };

  const generateNewFilename = useCallback(() => {
      const ts = format(new Date(), 'yyyyMMdd_kkmmss');
      setFilename(`${t('export.troncReport')}_${ts}.csv`);
  }, [setFilename, t]);

  useEffect(() => {
      if (!start) {
          setFilename(undefined);
          return;
      }
      generateNewFilename();
      setOpen(true);
  }, [start, generateNewFilename]);

  const onClose = () => {
      setOpen(false);
      if (onComplete) {
          onComplete();
      }
      setFilename(undefined);
  }

  const onDownload = () => {
    // create a fake link to initiate a download
    const link = document.createElement('a');
    link.href = createFileUri();
    link.setAttribute('target', '_blank');
    link.setAttribute('download', filename || 'export.csv');
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          maxWidth: '650px',
          borderRadius: '8px'
        }
      }}
    >
      <Box>
      <DialogTitle
        sx={{
          textAlign: 'center',
          fontWeight: 600, 
          marginTop: '32px'
        }}
      >
        {colorMode === 'light' ? <SuccessIcon fontSize="56px"/> : <WhiteSuccessIcon fontSize="56px"/>}
        <br />
        {t('export.exportingTronc')}
      </DialogTitle>
      <DialogContent sx={contentStyle}>
          <IconButton
              sx={{position: 'absolute', top: '8px', left: '600px', color: "text.secondary"}}
              onClick={onClose}
          >
            <CloseOutlinedIcon />
          </IconButton>
        <DialogContentText sx={{marginBottom: '8px'}}>
          {t('export.yourReportIsAvailable')}
          <br />
          {t('export.itIsNowReady')}
        </DialogContentText>
        <Box>
          <Card
            variant="outlined"
            sx={cardStyle}
          >
            <CardContent sx={{padding: '16px !important'}}>
              <div style={{ display: 'flex', textAlign: 'left', justifyContent: 'center' }}>
                {colorMode === 'light' ?
                  <SuccessIcon style={{ width: "20px", height: "20px", marginRight: "10px" }}/> :
                  <WhiteSuccessIcon fontSize="20px" style={{ width: "20px", height: "20px", marginRight: "10px" }}/>
                }
                <Typography component="span" sx={{ fontWeight: 400, fontSize: '14px' }}>
                  {filename || 'export.csv'}
                </Typography>
              </div>
            </CardContent>
          </Card>
        </Box>
        <DialogActions>
          <Button variant="contained" size="large" component="a" onClick={onDownload}>
            {t('actions.download')}
          </Button>
        </DialogActions>
      </DialogContent>
      </Box>
    </Dialog>
  );
};
